import { Link } from '../../components/Link';
import { ProviderArtwork } from '../../components/ProviderArtwork';
import type { ProviderProfile } from '../../features/catalog/providerProfiles';
import { fmtReq } from '../../lib/format';

/** What a provider is called on a public surface: a credited data brand, or an
 *  anonymous identity standing in for a marketplace account. */
export const providerKind = (profile: ProviderProfile): string =>
  profile.source.kind === 'brand' ? 'Credited source' : 'Marketplace provider';

/**
 * One provider in the rail: artwork, name, its endpoint count, and what it is.
 *
 * The same chip the home page's coverage rails use, so the directory reads as
 * an index rather than a wall of cards. Borders are drawn on two sides only and
 * the grid crops the outer pair, which is what keeps the rule between two cells
 * a single hairline.
 */
const ProviderRailChip = ({ profile }: { profile: ProviderProfile }) => (
  <Link
    to={`/providers/${profile.source.id}`}
    className="group flex min-w-0 flex-col gap-1 border-b border-r border-rule px-3 py-3 transition-colors hover:bg-wash"
  >
    <span className="flex min-w-0 items-center gap-2.5">
      <ProviderArtwork source={profile.source} size={24} />
      <span className="min-w-0 truncate text-13 font-medium group-hover:text-accent">
        {profile.source.name}
      </span>
      <span className="ml-auto shrink-0 pl-1 text-13 text-text-3">
        {profile.endpoints.length}
      </span>
    </span>
    <span className="truncate text-12 text-text-3">
      {providerKind(profile)}
      {profile.requests > 0 ? (
        <>
          <span className="mx-1.5">·</span>
          <span className="mono">{fmtReq(profile.requests)} req</span>
        </>
      ) : null}
    </span>
  </Link>
);

/** The bordered grid the chips sit in. */
export const ProviderRail = ({ profiles }: { profiles: ProviderProfile[] }) => (
  <div className="overflow-hidden rounded-12 border border-rule">
    <div className="-mb-px -mr-px grid grid-cols-1 s480:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {profiles.map((profile) => (
        <ProviderRailChip key={profile.source.id} profile={profile} />
      ))}
    </div>
  </div>
);
